import { useEffect, useState } from 'react'
import appsignal from '@/appsignal'

interface VersionResponse {
  version?: string
}

// public/uuid.json bevat deze placeholder. Bij een build overschrijft
// vite.config.js hem met de echte BUILD_ID, maar de dev-server bouwt niets en
// serveert dus de placeholder. Krijgen we die terug, dan weten we niet welke
// versie de server draait -- en melden we niets, net als wanneer we onze eigen
// build-id niet kunnen lezen. Anders staat de banner in local dev permanent aan.
const PLACEHOLDER_VERSION = 'UUID'

export default function useVersionChecker(): boolean {
  const [isOutdated, setIsOutdated] = useState<boolean>(false)

  useEffect(() => {
    // root.tsx rendert dit als data-version; zonder eigen build-id kunnen we
    // niets vergelijken en melden we dus niets.
    const currentUuid: string = document.getElementById('x-uuid')?.getAttribute('data-version') || ''
    if (!currentUuid) return

    const checkVersion = async (): Promise<void> => {
      try {
        const res = await fetch('/uuid.json', { cache: 'no-store' })
        const { version: newUuid }: VersionResponse = await res.json()

        if (newUuid && newUuid !== PLACEHOLDER_VERSION && newUuid !== currentUuid) {
          setIsOutdated(true)
        }
      } catch (err) {
        appsignal?.sendError(err instanceof Error ? err : new Error('Version check failed'), (span) => {
          span.setTags({ type: 'version-check-failure' })
          span.setParams({ url: window.location.href })
          return span
        })
      }
    }

    void checkVersion()

    // Een PWA blijft dagen open staan: opnieuw kijken zodra de tab terugkomt,
    // anders blijft een gestrande client op zijn oude bundle zitten.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void checkVersion()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  return isOutdated
}
